import React, { useState, useEffect } from 'react';

interface FlowStep {
  id: string;
  label: string;
  type: 'user' | 'frontend' | 'api' | 'database' | 'storage';
  active?: boolean;
  data?: any;
}

export default function ArchitectureView() {
  const [activeFlow, setActiveFlow] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [flowData, setFlowData] = useState<any>({});
  const [isPlaying, setIsPlaying] = useState(false);

  const flows = {
    create: {
      name: 'Create File Receiver',
      steps: [
        { id: 'user-input', label: 'User enters name', type: 'user' },
        { id: 'frontend', label: 'FastFilesLanding.tsx', type: 'frontend' },
        { id: 'generate', label: 'Generate slug: {id}/{name}', type: 'frontend' },
        { id: 'api-call', label: 'POST /api/quick-upload-page', type: 'api' },
        { id: 'd1-write', label: 'Store in D1 Database', type: 'database' },
        { id: 'response', label: 'Return upload link', type: 'api' },
        { id: 'localstorage', label: 'Save to localStorage', type: 'frontend' }
      ]
    },
    upload: {
      name: 'Upload Files',
      steps: [
        { id: 'select', label: 'User selects files', type: 'user' },
        { id: 'modal', label: 'Show upload modal', type: 'frontend' },
        { id: 'formdata', label: 'Create FormData', type: 'frontend' },
        { id: 'upload-api', label: 'POST /api/business-upload', type: 'api' },
        { id: 'r2-store', label: 'Store in R2 Bucket', type: 'storage' },
        { id: 'd1-meta', label: 'Save metadata in D1', type: 'database' },
        { id: 'success', label: 'Return success', type: 'api' },
        { id: 'share-modal', label: 'Show share modal', type: 'frontend' }
      ]
    },
    display: {
      name: 'Display Files',
      steps: [
        { id: 'page-load', label: 'Page loads', type: 'user' },
        { id: 'pages-func', label: 'Pages Function [id]/[name].js', type: 'frontend' },
        { id: 'get-link', label: 'GET /api/upload-links/{slug}', type: 'api' },
        { id: 'd1-query', label: 'Query D1 for link data', type: 'database' },
        { id: 'list-files', label: 'GET /api/list-files/{slug}', type: 'api' },
        { id: 'r2-list', label: 'List R2 objects with prefix', type: 'storage' },
        { id: 'generate-html', label: 'Generate HTML with files', type: 'frontend' },
        { id: 'render', label: 'Render page to user', type: 'user' }
      ]
    },
    download: {
      name: 'Download File',
      steps: [
        { id: 'click', label: 'User clicks file', type: 'user' },
        { id: 'download-api', label: 'GET /api/download?key={key}', type: 'api' },
        { id: 'r2-fetch', label: 'Fetch from R2', type: 'storage' },
        { id: 'stream', label: 'Stream file to browser', type: 'api' },
        { id: 'save', label: 'Browser saves file', type: 'user' }
      ]
    }
  };

  useEffect(() => {
    if (isPlaying && activeFlow) {
      const flow = flows[activeFlow as keyof typeof flows];
      if (flow && currentStep < flow.steps.length) {
        const timer = setTimeout(() => {
          // Simulate data flow
          const step = flow.steps[currentStep];
          updateFlowData(activeFlow, currentStep);
          setCurrentStep(currentStep + 1);
        }, 1000);
        return () => clearTimeout(timer);
      } else if (currentStep >= flow?.steps.length) {
        setIsPlaying(false);
      }
    }
  }, [isPlaying, activeFlow, currentStep]);

  const updateFlowData = (flow: string, step: number) => {
    const mockData: any = {
      create: [
        { input: 'Wedding Photos' },
        { component: 'FastFilesLanding.tsx:handleCreate()' },
        { slug: 'xyz123/wedding-photos' },
        { request: { businessName: 'Wedding Photos', slug: 'xyz123/wedding-photos' } },
        { database: 'INSERT INTO upload_links...' },
        { response: { id: 'uuid-123', slug: 'xyz123/wedding-photos' } },
        { localStorage: 'myFileReceivers: [...]' }
      ],
      upload: [
        { files: ['IMG_001.jpg', 'IMG_002.jpg'] },
        { modal: 'Collecting uploader info...' },
        { formData: 'file + metadata' },
        { api: 'POST with FormData' },
        { r2: 'business/xyz123/wedding-photos/IMG_001.jpg' },
        { d1: 'INSERT INTO uploaded_files...' },
        { success: true },
        { shareLink: 'https://happyhumantools.com/files/xyz123/wedding-photos' }
      ],
      display: [
        { url: '/files/xyz123/wedding-photos' },
        { pagesFunction: 'Parsing params: id=xyz123, name=wedding-photos' },
        { apiCall: 'Fetching link data...' },
        { linkData: { name: 'Wedding Photos', expires_at: '2025-12-31' } },
        { listCall: 'Fetching files...' },
        { files: ['IMG_001.jpg', 'IMG_002.jpg'] },
        { html: 'Generating thumbnails...' },
        { rendered: '✓ Page ready' }
      ],
      download: [
        { click: 'IMG_001.jpg' },
        { key: 'business/xyz123/wedding-photos/IMG_001.jpg' },
        { r2Fetch: 'Retrieving from R2...' },
        { stream: 'Sending file...' },
        { downloaded: '✓ File saved' }
      ]
    };

    setFlowData((prev: any) => ({
      ...prev,
      [`${flow}-${step}`]: mockData[flow]?.[step] || {}
    }));
  };

  const startFlow = (flowName: string) => {
    setActiveFlow(flowName);
    setCurrentStep(0);
    setFlowData({});
    setIsPlaying(true);
  };

  const getStepStyle = (flowName: string, stepIndex: number) => {
    if (activeFlow !== flowName) return '';
    if (stepIndex < currentStep) return 'opacity-100 scale-100';
    if (stepIndex === currentStep) return 'opacity-100 scale-110 animate-pulse';
    return 'opacity-30 scale-95';
  };

  const getConnectionStyle = (flowName: string, stepIndex: number) => {
    if (activeFlow !== flowName) return 'bg-gray-300';
    if (stepIndex < currentStep) return 'bg-green-500 animate-flow';
    return 'bg-gray-300';
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'user': return 'bg-blue-500';
      case 'frontend': return 'bg-indigo-500';
      case 'api': return 'bg-yellow-500';
      case 'database': return 'bg-purple-500';
      case 'storage': return 'bg-pink-500';
      default: return 'bg-gray-500';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'user': return '👤';
      case 'frontend': return '🖥️';
      case 'api': return '🔌';
      case 'database': return '🗄️';
      case 'storage': return '📦';
      default: return '📍';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <style>{`
        @keyframes flow {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-flow::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, transparent, white, transparent);
          animation: flow 1s ease-in-out;
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            FastFiles Architecture
          </h1>
          <p className="text-xl text-gray-600">
            Interactive visualization of data flows and system components
          </p>
        </div>

        {/* Flow Selection */}
        <div className="flex justify-center gap-4 mb-12">
          {Object.entries(flows).map(([key, flow]) => (
            <button
              key={key}
              onClick={() => startFlow(key)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 ${
                activeFlow === key
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 shadow hover:shadow-md'
              }`}
            >
              {flow.name}
            </button>
          ))}
        </div>

        {/* Flow Visualization */}
        {Object.entries(flows).map(([flowKey, flow]) => (
          <div
            key={flowKey}
            className={`mb-12 transition-all duration-500 ${
              activeFlow === flowKey ? 'opacity-100' : 'opacity-50'
            }`}
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-6">{flow.name}</h2>

            <div className="relative">
              {/* Flow Steps */}
              <div className="flex items-center justify-between overflow-x-auto pb-4">
                {flow.steps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    {/* Step Node */}
                    <div
                      className={`relative transition-all duration-500 ${getStepStyle(flowKey, index)}`}
                    >
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl shadow-lg ${getTypeColor(
                            step.type
                          )}`}
                        >
                          {getTypeIcon(step.type)}
                        </div>
                        <div className="mt-2 text-center">
                          <div className="text-sm font-semibold text-gray-700 max-w-32">
                            {step.label}
                          </div>
                          {flowData[`${flowKey}-${index}`] && (
                            <div className="mt-1 text-xs text-gray-500 bg-white rounded-lg px-2 py-1 shadow">
                              <pre className="whitespace-pre-wrap">
                                {JSON.stringify(flowData[`${flowKey}-${index}`], null, 2).slice(0, 50)}...
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Connection Line */}
                    {index < flow.steps.length - 1 && (
                      <div className="flex-1 mx-2">
                        <div
                          className={`h-1 relative overflow-hidden transition-colors duration-500 ${getConnectionStyle(
                            flowKey,
                            index
                          )}`}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Progress Indicator */}
              {activeFlow === flowKey && (
                <div className="mt-6 bg-white rounded-lg p-4 shadow">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-600">
                      Step {currentStep} of {flow.steps.length}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setCurrentStep(0);
                          setFlowData({});
                          setIsPlaying(true);
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                      >
                        Restart
                      </button>
                      <button
                        onClick={() => setIsPlaying(!isPlaying)}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                      >
                        {isPlaying ? 'Pause' : 'Play'}
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(currentStep / flow.steps.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Component Legend */}
        <div className="mt-12 bg-white rounded-xl p-6 shadow-lg">
          <h3 className="text-xl font-bold text-gray-800 mb-4">System Components</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { type: 'user', label: 'User Action', color: 'bg-blue-500' },
              { type: 'frontend', label: 'Frontend', color: 'bg-indigo-500' },
              { type: 'api', label: 'API Call', color: 'bg-yellow-500' },
              { type: 'database', label: 'D1 Database', color: 'bg-purple-500' },
              { type: 'storage', label: 'R2 Storage', color: 'bg-pink-500' }
            ].map(item => (
              <div key={item.type} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full ${item.color}`} />
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* File Paths */}
        <div className="mt-8 bg-white rounded-xl p-6 shadow-lg">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Key Files</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-sm">
            <div>
              <strong className="text-indigo-600">Frontend:</strong>
              <ul className="mt-2 space-y-1 text-gray-600">
                <li>• /src/components/FastFilesLanding.tsx</li>
                <li>• /src/components/FastFilesDashboard.tsx</li>
                <li>• /functions/files/[id]/[name].js</li>
              </ul>
            </div>
            <div>
              <strong className="text-yellow-600">Backend:</strong>
              <ul className="mt-2 space-y-1 text-gray-600">
                <li>• /workers/d1-api/index.js</li>
                <li>• /workers/d1-schema.sql</li>
                <li>• CloudFlare R2: happyhumantools</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}